// src/lib/phone.js — phone-number formatting shared across components.

// Normalise any phone number for display:
// strips +995 / 00995 prefix, removes spaces/dashes, then formats
// Georgian mobile (9 digits starting with 5): 5XX XXX XXX
// Georgian landline (9 digits starting with other): XXX XXX XXX
// anything else: returned as-is (no garbling of foreign numbers)
export function fmtPhone(raw) {
  if (!raw) return ''
  let digits = raw.replace(/[\s\-().]/g, '')
  // strip country code
  if (digits.startsWith('+995'))  digits = digits.slice(4)
  else if (digits.startsWith('00995')) digits = digits.slice(5)
  if (digits.length === 9) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
  }
  return fmtForeign(raw) || raw // foreign: its own plan if we have one, else don't mangle
}

// ─── Foreign numbers, in their own shape (owner, 2026-09-09) ───────────────────
// "Georgian number and others too in their native format." A Russian number is read as
// XXX XXX-XX-XX and a Ukrainian one as XX XXX XX XX; grouping either of them in the
// Georgian 3-3-3 would be as unreadable as no grouping at all, and more confident about it.
//
// ⚠️ THE DEFAULT IS STILL TO CHANGE NOTHING — the line above depends on this returning a
// falsy value for anything it does not recognise, which is what keeps `fmtPhone`'s original
// promise ("no garbling of foreign numbers") true for every country not listed here.
//
// Each entry is national-significant-number LENGTH → group sizes, so a number that is not
// the length its country's plan expects (a typo, a short code, a landline where we only know
// the mobile shape) falls through untouched rather than being padded into a shape it does
// not have. The list is short on purpose: every row is a plan we can state.
//
// 📌 If this ever has to be right for every country, the answer is `libphonenumber-js`
// (~25 kB gzipped with the `min` metadata), not a longer table. It was deliberately NOT
// added: an unlisted country already fails safe, and this bundle is watched.
const PLANS = {
  7: { 10: [3, 3, 2, 2] },       // Russia / Kazakhstan
  380: { 9: [2, 3, 2, 2] },      // Ukraine
  374: { 8: [2, 3, 3] },         // Armenia
  994: { 9: [2, 3, 2, 2] },      // Azerbaijan
  375: { 9: [2, 3, 2, 2] },      // Belarus
  90: { 10: [3, 3, 2, 2] },      // Turkey
  972: { 9: [2, 3, 4] },         // Israel (mobile 5X XXX XXXX)
  44: { 10: [4, 6] },            // UK (mobile 7XXX XXXXXX)
  1: { 10: [3, 3, 4] },          // US / Canada
  48: { 9: [3, 3, 3] },          // Poland
  30: { 10: [3, 3, 4] },         // Greece
  971: { 9: [2, 3, 4] },         // UAE
  91: { 10: [5, 5] },            // India
  86: { 11: [3, 4, 4] },         // China
  39: { 10: [3, 3, 4] },         // Italy
  33: { 9: [1, 2, 2, 2, 2] },    // France
  34: { 9: [3, 2, 2, 2] },       // Spain
}
// Longest first, so 995 can never be read as 99 and 1 is tried last.
const CODES = Object.keys(PLANS).sort((a, b) => b.length - a.length)

function fmtForeign(raw) {
  const s = String(raw).trim()
  // ⚠️ AN EXTENSION OR A NOTE IS NOT PART OF THE NUMBER. If the value carries anything but
  // digits and the usual separators, we are not confident we are looking at a phone number
  // alone, so nothing is touched.
  if (/[^\d+\s().-]/.test(s)) return null
  // ⚠️ THE `+` IS REQUIRED, and this is the guard that stops a local typo being reported as a
  // foreign number: without it an 11-digit Georgian mistype starting 7 would print as a
  // Russian mobile. `00` is the same declaration dialled the old way.
  const intl = s.startsWith('+') || s.startsWith('00')
  if (!intl) return null
  let digits = s.replace(/\D/g, '')
  if (s.startsWith('00')) digits = digits.slice(2)
  for (const cc of CODES) {
    if (!digits.startsWith(cc)) continue
    const rest = digits.slice(cc.length)
    const sizes = PLANS[cc][rest.length]
    if (!sizes) return null      // right country, unexpected length — say nothing.
    const out = []
    let i = 0
    for (const n of sizes) { out.push(rest.slice(i, i + n)); i += n }
    return `+${cc} ${out.join(' ')}`
  }
  return null
}

// ─── Monthly plan ──────────────────────────────────────────────────────────────
// A number's plan runs PLAN_DAYS from plan_started_at. Days left can go ≤ 0 (expired).
export const PLAN_DAYS = 30

// null  → no plan activated
// >0    → days remaining
// ≤0    → expired (e.g. 0 = expires today, -2 = expired 2 days ago)
export function planDaysLeft(planStartedAt) {
  if (!planStartedAt) return null
  const start   = new Date(planStartedAt).getTime()
  const elapsed = Math.floor((Date.now() - start) / 86400000)
  return PLAN_DAYS - elapsed
}

// Tailwind text/bg classes for a days-left badge: green >7, amber 1–7, red ≤0.
export function planBadgeClass(daysLeft) {
  if (daysLeft == null) return ''
  if (daysLeft <= 0) return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
  if (daysLeft <= 7) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
  return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
}

// ─── Duplicate detection ───────────────────────────────────────────────────────
// 🚨 A STRING COMPARE DOES NOT FIND DUPLICATES, and that is not theoretical. The two
// `Aleksandra` agent records that had to be merged on 2026-08-29 held her number as
// `+995 592 79 15 99` and `592791599` — the same person, the same phone, and not one
// character in common at the same position. Whoever added her the second time saw no warning
// because nothing normalised before comparing.
//
// The key is: keep digits only, drop the Georgian country code, and take the last 9. Numbers
// shorter than 9 digits (an extension, a half-typed field) return null and never match — a
// partial number matching everything would be worse than no check at all.
export function phoneKey(raw) {
  if (!raw) return null
  let d = String(raw).replace(/\D/g, '')
  if (d.startsWith('995')) d = d.slice(3)
  else if (d.startsWith('00995')) d = d.slice(5)
  if (d.length > 9) d = d.slice(-9)          // any other country code / stray prefix
  return d.length === 9 ? d : null
}

// Agents already holding this PERSONAL number, excluding `selfId`.
//
// 🚨 THE WORK NUMBER IS DELIBERATELY NOT CHECKED (owner, 2026-08-29). სამსახურის ტელ. is a
// company SIM that moves to the next person when someone leaves, so it is SUPPOSED to appear
// on many agents over time. Measured that day: 10 work numbers were held by more than one
// agent and one by six of them. Warning on it fired on every reassignment and named five
// former colleagues — a warning that cries on correct data teaches people to dismiss warnings.
// Only one agent can hold it at a time anyway, which phone_numbers enforces separately.
//
// პირადი ტელ. is the person, and the data agrees: zero duplicates across the roster, maximum
// one agent per number. That is the field where a repeat means "this person already exists".
// The legacy `phone` column is not consulted — it is empty on all 84 agents.
export function findPhoneMatches(numbers, agents = [], selfId = null) {
  const keys = new Set((numbers || []).map(phoneKey).filter(Boolean))
  if (!keys.size) return []
  return agents.filter(a => {
    if (!a || a.id === selfId) return false
    const k = phoneKey(a.phone_personal)
    return k && keys.has(k)
  })
}

// The national ID (პირადი ნომერი) — the one identifier that is unique by definition rather
// than by convention. Compared on digits only, so spacing or a stray dash cannot hide a match.
export function idKey(raw) {
  if (!raw) return null
  const d = String(raw).replace(/\D/g, '')
  return d.length >= 9 ? d : null
}

export function findIdMatches(idNumber, agents = [], selfId = null) {
  const k = idKey(idNumber)
  if (!k) return []
  return agents.filter(a => a && a.id !== selfId && idKey(a.id_number) === k)
}
